/**
 * Piece sequence generation.
 *
 * `GameState` only ever talks to the `PieceGenerator` interface. The 7-bag rule
 * lives in `SevenBagGenerator` and nowhere else — swap in any other
 * implementation (single-piece drills, seeded replays, an adversarial sequence
 * for the hackathon twist) without touching `core/game.ts`.
 */

import { PIECE_TYPES, type PieceType } from './piece.ts';

export interface PieceGenerator {
  /** Take the next piece out of the sequence. */
  next(): PieceType;
  /** Peek at the upcoming `count` pieces without consuming them. */
  preview(count: number): PieceType[];
  /** Optional: return to a fresh sequence. Called on restart when present. */
  reset?(): void;
}

/** Deterministic RNG source. Returns a float in [0, 1). */
export type RandomFn = () => number;

/** Small, fast, seedable PRNG so replays and tests can be reproducible. */
export function mulberry32(seed: number): RandomFn {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Guideline "random bag": every permutation of the 7 tetrominoes is dealt out
 * before a new bag is shuffled, so droughts are bounded.
 */
export class SevenBagGenerator implements PieceGenerator {
  private readonly random: RandomFn;
  private queue: PieceType[] = [];

  constructor(random: RandomFn = Math.random) {
    this.random = random;
  }

  next(): PieceType {
    this.fill(1);
    return this.queue.shift() as PieceType;
  }

  preview(count: number): PieceType[] {
    this.fill(count);
    return this.queue.slice(0, count);
  }

  reset(): void {
    this.queue = [];
  }

  private fill(count: number): void {
    while (this.queue.length < count) {
      this.queue.push(...this.shuffledBag());
    }
  }

  private shuffledBag(): PieceType[] {
    const bag = [...PIECE_TYPES];
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]];
    }
    return bag;
  }
}

/** Seven-bag whose reset returns to the same seed, useful for paired runs. */
export class SeededSevenBagGenerator implements PieceGenerator {
  private generator: SevenBagGenerator;

  constructor(private readonly seed: number) {
    this.generator = this.fresh();
  }

  next(): PieceType {
    return this.generator.next();
  }

  preview(count: number): PieceType[] {
    return this.generator.preview(count);
  }

  reset(): void {
    this.generator = this.fresh();
  }

  private fresh(): SevenBagGenerator {
    return new SevenBagGenerator(mulberry32(this.seed));
  }
}

/** Mirrors each freshly shuffled seven-bag supplied by another generator. */
export class MirroredBagGenerator implements PieceGenerator {
  private queue: PieceType[] = [];

  constructor(private readonly source: PieceGenerator) {}

  next(): PieceType {
    this.fill(1);
    return this.queue.shift() as PieceType;
  }

  preview(count: number): PieceType[] {
    this.fill(count);
    return this.queue.slice(0, count);
  }

  reset(): void {
    this.queue = [];
    this.source.reset?.();
  }

  private fill(count: number): void {
    while (this.queue.length < count) {
      const bag = Array.from({ length: PIECE_TYPES.length }, () => this.source.next());
      this.queue.push(...bag.reverse());
    }
  }
}

/** Repeats a fixed sequence. Handy for tests and scripted scenarios. */
export class FixedSequenceGenerator implements PieceGenerator {
  private index = 0;

  constructor(private readonly sequence: readonly PieceType[]) {
    if (sequence.length === 0) throw new Error('FixedSequenceGenerator needs at least one piece');
  }

  next(): PieceType {
    const piece = this.sequence[this.index % this.sequence.length];
    this.index++;
    return piece;
  }

  preview(count: number): PieceType[] {
    const out: PieceType[] = [];
    for (let i = 0; i < count; i++) {
      out.push(this.sequence[(this.index + i) % this.sequence.length]);
    }
    return out;
  }

  reset(): void {
    this.index = 0;
  }
}
